/**
 * Selectores puros para el detalle de una Propuesta (Release 2).
 *
 * Reglas:
 *  - Avance se mide en HI cerrados / total HI. Aplica a todas las capas superiores.
 *  - Lente Cliente → solo hitos con isFormal=true
 *  - Lente Gerencia → solo hitos con isClave=true
 *  - Lente Interno → todos los hitos (isImplementacion=true por default = todos)
 *
 *  Tags se heredan por UNIÓN desde compromisos hacia arriba.
 */
import type {
  Compromiso,
  Hito,
  Iniciativa,
  Lente,
  PropuestaDetail,
  ScopeFilter,
  Tag,
} from '@/types';

/** Filtra hitos según el lente activo (cliente/gerencia/interno). */
export function hitosPorLente(hitos: Hito[], lente: Lente): Hito[] {
  if (lente === 'cliente') return hitos.filter((h) => h.isFormal);
  if (lente === 'gerencia') return hitos.filter((h) => h.isClave);
  return hitos;
}

/** Hitos pertenecientes a un parent específico (propuesta/ev/ini/compromiso). */
export function hitosDeParent(
  hitos: Hito[],
  parentType: 'propuesta' | 'ev' | 'iniciativa' | 'compromiso',
  parentId: string,
): Hito[] {
  return hitos.filter((h) => h.parentType === parentType && h.parentId === parentId);
}

/**
 * Avance de hitos.
 * Regla R4: los hitos en estado `suspendido` NO cuentan ni en `total` ni en `done`.
 *   pct = entregados / (total - suspendidos)
 */
export function avance(hitos: Hito[]): { done: number; total: number; pct: number } {
  const activos = hitos.filter((h) => h.estado !== 'suspendido');
  const total = activos.length;
  const done = activos.filter((h) => h.estado === 'entregado').length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

/**
 * Un compromiso está SUSPENDIDO cuando TODOS sus hitos están en `suspendido`
 * (y tiene al menos uno). No cuenta para el total de progreso de compromisos.
 */
export function compromisoEstaSuspendido(detail: PropuestaDetail, compromisoId: string): boolean {
  const hitosComp = detail.hitos.filter(
    (h) => h.parentType === 'compromiso' && h.parentId === compromisoId,
  );
  if (hitosComp.length === 0) return false;
  return hitosComp.every((h) => h.estado === 'suspendido');
}

/**
 * Un compromiso se considera COMPLETADO cuando TODOS sus hitos no-suspendidos
 * están en `entregado` (y tiene al menos uno no-suspendido).
 *   Ej: 2 entregados + 1 suspendido → completado.
 *   Ej: todos suspendidos → suspendido (NO completado).
 */
export function compromisoEsCompletado(detail: PropuestaDetail, compromisoId: string): boolean {
  const hitosComp = detail.hitos.filter(
    (h) => h.parentType === 'compromiso' && h.parentId === compromisoId,
  );
  const activos = hitosComp.filter((h) => h.estado !== 'suspendido');
  if (activos.length === 0) return false;
  return activos.every((h) => h.estado === 'entregado');
}

/**
 * Avance de un conjunto de compromisos (en cantidad).
 * Regla R4: compromisos suspendidos NO cuentan en el total.
 */
export function avanceCompromisos(
  detail: PropuestaDetail,
  compromisoIds: string[],
): { done: number; total: number; pct: number } {
  const activos = compromisoIds.filter((id) => !compromisoEstaSuspendido(detail, id));
  const total = activos.length;
  const done = activos.filter((id) => compromisoEsCompletado(detail, id)).length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

/* ===== Propagación de estado bloqueado ===== */

/**
 * Health (R/Y/G/N) de un conjunto de hitos:
 *  - red:     ≥1 hito en estado 'bloqueado'
 *  - green:   ≥1 hito activo Y todos los activos están entregados
 *  - yellow:  hay pendientes/on_track sin bloqueos
 *  - neutral: no hay hitos activos
 */
export function healthDeHitos(hitos: Hito[]): 'red' | 'yellow' | 'green' | 'neutral' {
  const hayBloqueado = hitos.some((h) => h.estado === 'bloqueado');
  if (hayBloqueado) return 'red';
  const activos = hitos.filter((h) => h.estado !== 'suspendido');
  if (activos.length === 0) return 'neutral';
  if (activos.every((h) => h.estado === 'entregado')) return 'green';
  return 'yellow';
}

/** ¿Algún hito directo de este compromiso está bloqueado? */
export function compromisoTieneBloqueo(detail: PropuestaDetail, compromisoId: string): boolean {
  return detail.hitos.some(
    (h) =>
      h.estado === 'bloqueado' &&
      h.parentType === 'compromiso' &&
      h.parentId === compromisoId,
  );
}

/** Health propagado de un compromiso (mira sus hitos directos). */
export function healthCompromiso(detail: PropuestaDetail, compromisoId: string): 'red' | 'yellow' | 'green' | 'neutral' {
  return healthDeHitos(
    detail.hitos.filter((h) => h.parentType === 'compromiso' && h.parentId === compromisoId),
  );
}

/** Health propagado de una iniciativa (subárbol completo). */
export function healthIniciativa(detail: PropuestaDetail, iniciativaId: string): 'red' | 'yellow' | 'green' | 'neutral' {
  return healthDeHitos(hitosDeIniciativa(detail, iniciativaId));
}

/** Health propagado de una EV (subárbol completo). */
export function healthEv(detail: PropuestaDetail, evId: string): 'red' | 'yellow' | 'green' | 'neutral' {
  return healthDeHitos(hitosDeEv(detail, evId));
}

/**
 * ¿Alguna parte del subárbol de la iniciativa tiene bloqueo?
 * Incluye hitos directos de la iniciativa + bloqueo en cualquiera de sus compromisos.
 */
export function iniciativaTieneBloqueo(detail: PropuestaDetail, iniciativaId: string): boolean {
  if (
    detail.hitos.some(
      (h) =>
        h.estado === 'bloqueado' &&
        h.parentType === 'iniciativa' &&
        h.parentId === iniciativaId,
    )
  ) {
    return true;
  }
  const compromisoIds = detail.compromisos
    .filter((c) => c.iniciativaId === iniciativaId)
    .map((c) => c.id);
  return compromisoIds.some((cid) => compromisoTieneBloqueo(detail, cid));
}

/**
 * ¿Alguna parte del subárbol de la EV tiene bloqueo?
 * Incluye hitos directos de la EV + bloqueo en cualquiera de sus iniciativas (recursivo).
 */
export function evTieneBloqueo(detail: PropuestaDetail, evId: string): boolean {
  if (
    detail.hitos.some(
      (h) =>
        h.estado === 'bloqueado' && h.parentType === 'ev' && h.parentId === evId,
    )
  ) {
    return true;
  }
  const inisDeEv = detail.iniciativas.filter((i) => i.entregaValorId === evId);
  return inisDeEv.some((i) => iniciativaTieneBloqueo(detail, i.id));
}

/**
 * Avance para una iniciativa = hitos directos de la iniciativa + hitos de todos sus compromisos.
 * (incluye HI dinámicos = items)
 */
export function hitosDeIniciativa(detail: PropuestaDetail, iniId: string): Hito[] {
  const compromisoIds = detail.compromisos
    .filter((c) => c.iniciativaId === iniId)
    .map((c) => c.id);
  return detail.hitos.filter(
    (h) =>
      (h.parentType === 'iniciativa' && h.parentId === iniId) ||
      (h.parentType === 'compromiso' && compromisoIds.includes(h.parentId)),
  );
}

/** Avance para una EV = hitos de la EV + hitos de todas sus iniciativas (recursivo). */
export function hitosDeEv(detail: PropuestaDetail, evId: string): Hito[] {
  const iniIds = detail.iniciativas.filter((i) => i.entregaValorId === evId).map((i) => i.id);
  const compromisoIds = detail.compromisos
    .filter((c) => iniIds.includes(c.iniciativaId))
    .map((c) => c.id);
  return detail.hitos.filter(
    (h) =>
      (h.parentType === 'ev' && h.parentId === evId) ||
      (h.parentType === 'iniciativa' && iniIds.includes(h.parentId)) ||
      (h.parentType === 'compromiso' && compromisoIds.includes(h.parentId)),
  );
}

/** Hitos totales de la propuesta (todos los niveles). */
export function todosLosHitos(detail: PropuestaDetail): Hito[] {
  return detail.hitos;
}

/** Hitos visibles dado el scope + lente activos. */
export function hitosVisibles(
  detail: PropuestaDetail,
  scope: ScopeFilter,
  lente: Lente,
): Hito[] {
  let base: Hito[];
  if (!scope.type || !scope.id) {
    base = detail.hitos;
  } else if (scope.type === 'ev') {
    base = hitosDeEv(detail, scope.id);
  } else {
    base = hitosDeIniciativa(detail, scope.id);
  }
  return hitosPorLente(base, lente);
}

/** Compromisos visibles según scope + (opcionalmente) lente. */
export function compromisosVisibles(
  detail: PropuestaDetail,
  scope: ScopeFilter,
  lente: Lente,
): Compromiso[] {
  let comps: Compromiso[];
  if (!scope.type || !scope.id) {
    comps = detail.compromisos;
  } else if (scope.type === 'ev') {
    const iniIds = detail.iniciativas
      .filter((i) => i.entregaValorId === scope.id)
      .map((i) => i.id);
    comps = detail.compromisos.filter((c) => iniIds.includes(c.iniciativaId));
  } else {
    comps = detail.compromisos.filter((c) => c.iniciativaId === scope.id);
  }
  // Lente Cliente: filtra compromisos que tienen al menos un hito formal
  if (lente === 'cliente') {
    const hitosLente = hitosPorLente(detail.hitos, lente);
    const compConFormal = new Set(
      hitosLente
        .filter((h) => h.parentType === 'compromiso')
        .map((h) => h.parentId),
    );
    // Y los compromisos cuya iniciativa tenga al menos un hito formal
    const iniConFormal = new Set(
      hitosLente
        .filter((h) => h.parentType === 'iniciativa')
        .map((h) => h.parentId),
    );
    comps = comps.filter(
      (c) => compConFormal.has(c.id) || iniConFormal.has(c.iniciativaId) || tieneHitosFormales(detail, c.id),
    );
  } else if (lente === 'gerencia') {
    // Similar pero con isClave
    const hitosLente = hitosPorLente(detail.hitos, lente);
    const compConClave = new Set(
      hitosLente
        .filter((h) => h.parentType === 'compromiso')
        .map((h) => h.parentId),
    );
    comps = comps.filter((c) => compConClave.has(c.id) || tieneHitosClave(detail, c.id));
  }
  return comps;
}

function tieneHitosFormales(detail: PropuestaDetail, compromisoId: string): boolean {
  return detail.hitos.some(
    (h) => h.parentType === 'compromiso' && h.parentId === compromisoId && h.isFormal,
  );
}
function tieneHitosClave(detail: PropuestaDetail, compromisoId: string): boolean {
  return detail.hitos.some(
    (h) => h.parentType === 'compromiso' && h.parentId === compromisoId && h.isClave,
  );
}

/** Tags efectivos para una entidad (unión desde compromisos hacia arriba). */
export function tagsHeredados(detail: PropuestaDetail, scopeType: 'ev' | 'ini' | 'compromiso', id: string): string[] {
  let compromisos: Compromiso[];
  if (scopeType === 'compromiso') {
    const c = detail.compromisos.find((x) => x.id === id);
    return c?.tagIds ?? [];
  }
  if (scopeType === 'ini') {
    compromisos = detail.compromisos.filter((c) => c.iniciativaId === id);
  } else {
    const iniIds = detail.iniciativas.filter((i) => i.entregaValorId === id).map((i) => i.id);
    compromisos = detail.compromisos.filter((c) => iniIds.includes(c.iniciativaId));
  }
  const tagIds = new Set<string>();
  for (const c of compromisos) for (const tid of c.tagIds) tagIds.add(tid);
  return [...tagIds];
}

/** Etiqueta humana para el scope chip. */
export function scopeLabel(
  detail: PropuestaDetail,
  scope: ScopeFilter,
): { kind: string; name: string; color: string } | null {
  if (!scope.type || !scope.id) return null;
  if (scope.type === 'ev') {
    const ev = detail.entregasValor.find((e) => e.id === scope.id);
    if (!ev) return null;
    return { kind: 'Entrega de Valor', name: `${ev.codigo} · ${ev.nombre}`, color: ev.color };
  }
  const ini = detail.iniciativas.find((i) => i.id === scope.id);
  if (!ini) return null;
  const ev = detail.entregasValor.find((e) => e.id === ini.entregaValorId);
  return {
    kind: 'Iniciativa',
    name: `${ini.codigo} · ${ini.nombre}`,
    color: ev?.color || 'var(--brand)',
  };
}

/** Mapa rápido id → Tag. */
export function tagMap(tags: Tag[]): Map<string, Tag> {
  return new Map(tags.map((t) => [t.id, t]));
}
